import { Component, OnInit, Input } from '@angular/core';
import { AppConfigService } from 'src/app/core/service/app-config.service';
import { State, process } from '@progress/kendo-data-query';
import { PayerBillingService } from 'src/app/core/service/payer-billing.service';
import { PayerBillingItemListService } from 'src/app/core/service/grid-data/grid-data-payer-billing-item.service';
import { DataStateChangeEvent, GridDataResult, SelectAllCheckboxState, ColumnReorderEvent, ColumnVisibilityChangeEvent, SelectionEvent } from '@progress/kendo-angular-grid';
import { MessageHandlerService } from 'src/app/core/utility/message-handler.service';
import { GridQueryUtils } from 'src/app/core/utility/grid-query-utils';
import { GridViewManager } from 'src/app/core/model/grid-view-manager';
import { TranslateService } from '@ngx-translate/core';
import { GridSettings, GridResizeEvent } from 'src/app/core/model/grid-settings';
import { GridStatePersistingService } from 'src/app/core/service/grid-state-persisting';
import { SettingService } from 'src/app/core/service/setting.service';
import { GridEventHandler } from 'src/app/core/model/grid-settings';
import { UntypedFormBuilder } from '@angular/forms';
import { IntlService } from '@progress/kendo-angular-intl';
import { DictionaryService } from 'src/app/core/service/dictionary.service';
import { ExcelExportData } from '@progress/kendo-angular-excel-export';
import { CaseService } from '../../core/service/case.service';

@Component({
  selector: 'app-invoice-item-list',
  templateUrl: './invoice-item-list.component.html',
  styleUrls: ['./invoice-item-list.component.scss']
})
export class InvoiceItemListComponent implements OnInit
{
  @Input() public invoice: any = null;

  public enableAdvanced = false;
  public gridView: GridDataResult;
  public gridData: any = {};
  public count: number;
  public pageSizes = [5, 10, 25, 50, 100, 1000];
  public selectedRecordKeys: number[] = [];
  public selectedInvoiceItemsRebuildId: any = [];
  public selectAllState: SelectAllCheckboxState = 'unchecked';
  public view: GridViewManager;
  public patientLabelText = '';
  public invoiceItemStatuses = [];
  public selectedInvoiceItem: any;
  public rebillInvoiceItemsDialogOpened = false;
  public rebillRestriction: string;
  public editedInvoiceItem: any;
  public deleteDialogOpened = false;
  public selectedInvoiceItemId: number;
  public selectedInvoiceItemName: string;
  public payerBillingIdentifierName = '';
  public excelFileName: string;

  /**
   * FormGroup for in-grid editing of invoice item.
   */
  public invoiceItemFormGroup: any = null;

  /**
   * Payer Billing list Kendo grid state.
   */
  public state: State = {
    skip: 0,
    // Initial filter descriptor
    filter: {
      logic: 'and',
      filters: []
    },
    sort: [
      { field: 'lastModifiedOn', dir: 'desc' }
    ]
  };

  private textAmountRequired: string;
  private textUnitsRequired: string;
  private textRateRequired: string;
  private textUnitsRateRequired: string;
  private items: any[] = [];

  constructor(
    private appConfigService: AppConfigService,
    private payerBillingService: PayerBillingService,
    private payerBillingItemListService: PayerBillingItemListService,
    private messageHandlerService: MessageHandlerService,
    private translateService: TranslateService,
    private gridStatePersistingService: GridStatePersistingService,
    private settingService: SettingService,
    private formBuilder: UntypedFormBuilder,
    public intl: IntlService,
    private dictionaryService: DictionaryService,
    private caseService: CaseService
  )
  {
    this.view = new GridViewManager(this.appConfigService.getConfig().pageSize);
    this.settingService.getCachedSessionSettings().subscribe({
      next: (sessionSetting) =>
      {
        this.patientLabelText = sessionSetting.patientLabel;
        if (sessionSetting.payerBillingIdentifier !== '')
        {
          this.dictionaryService.getCachedCommunityServiceIdentifierTypes().subscribe({
            next: (data) =>
            {
              this.payerBillingIdentifierName = data.find(x => x.id === parseInt(sessionSetting.payerBillingIdentifier)).displayName;
              this.initGridColumns();
            }
          });
        }
        else
        {
          this.initGridColumns();
        }
      }
    });
  }

  /**
   * Provide loading property from Service.
   */
  public get loading(): boolean
  {
    return this.payerBillingItemListService ? this.payerBillingItemListService.loading : false;
  }

  public ngOnInit(): void
  {
    this.invoiceItemStatuses = this.payerBillingService.invoiceItemStatuses;
    this.loadInvoiceItems(this.invoice.id);
    this.translateService.get([
      'MANUAL-AMOUNT-REQUIRED',
      'UNITS-REQUIRED',
      'RATE-REQUIRED-BILLABLE',
      'UNITS-RATE-REQUIRED'
    ]).subscribe({
      next: (res) =>
      {
        this.textAmountRequired = res['MANUAL-AMOUNT-REQUIRED'];
        this.textUnitsRequired = res['UNITS-REQUIRED'];
        this.textRateRequired = res['RATE-REQUIRED-BILLABLE'];
        this.textUnitsRateRequired = res['UNITS-RATE-REQUIRED'];
      }
    });
  }

  /**
      * Reorder grid column order event
      *
      * @param e ColumnReorderEvent event
      */
  public onReorder(e: ColumnReorderEvent): void
  {
    const reorderedColumn = this.view.columnsConfig.splice(e.oldIndex - 1, 1);
    this.view.columnsConfig.splice(e.newIndex - 1, 0, ...reorderedColumn);
    this.saveGrid();
  }

  /**
   * Grid onResize event.
   *
   * @param e Grid on resize column event.
   */
  public onResize(event: Array<GridResizeEvent>): void
  {
    event.forEach(item =>
    {
      const field = this.view.columnsConfig.find((col) => col.field === item.column.field);
      if (field !== undefined)
      {
        field.width = item.newWidth;
      }
    });
    this.saveGrid();
  }

  /**
   * Grid column visibility changed.
   *
   * @param event Grid column visibility event.
   */
  public onVisibilityChange(event: ColumnVisibilityChangeEvent): void
  {
    event.columns.forEach((column: any) =>
    {
      const field = this.view.columnsConfig.find((col) => col.field === column.field);
      if (field !== undefined)
      {
        field.hidden = column.hidden;
      }
    });
    this.saveGrid();
  }

  /**
   * Data change event handler.
   *
   * @param state State of grid operations
   */
  public dataStateChange(state: DataStateChangeEvent): void
  {
    this.state = state;
    this.saveGrid();
    this.executeQuery();
  }

  /**
   * Reset grid columns with default state
   */
  public resetGridColumns(): void
  {
    this.gridStatePersistingService.delete('grid-settings-invoice-item');
    this.initGridColumns();
    this.view.state.filter = { logic: 'and', filters: [] };
  }

  /**
   * Selected invoice items.
   */
  public onSelectedKeysChange(event: any): void
  {
    this.selectedRecordKeys = event;
    if (this.selectedRecordKeys.length === 1)
    {
      this.selectedInvoiceItem = this.gridView.data.filter(f => f.id == event[0])[0];
      if (this.selectedInvoiceItem && this.selectedInvoiceItem.canRebuildInvoiceItem)
      {
        this.selectedInvoiceItemsRebuildId = [this.selectedInvoiceItem.id];
      }
      else
      {
        this.selectedInvoiceItemsRebuildId = [];
      }
    }
    else
    {
      this.selectedInvoiceItem = null;
    }
  }

  public onSelectAllChange(checkedState: SelectAllCheckboxState)
  {
    if (checkedState === 'checked')
    {
      this.selectedInvoiceItemsRebuildId = this.gridView.data.filter((item) => (this.canRebuildInvoiceItem(item.invoiceItemStatus))).map((item) => item.id);
      this.selectAllState = 'checked';
      this.selectedInvoiceItem = null;
    }
    else
    {
      this.clearSelectedItems();
    }
  }

  /**
   * Provides the invoice items selected count. This controls the 'Select' button state and count display.
   *
   * @returns Number Total count of the selected invoice items.
   */
  public getSelectedCount(): number
  {
    return this.selectedInvoiceItemsRebuildId.length;
  }

  public rebillInvoiceItems()
  {
    // update PROCESSED and DENIED / CANCELED / VOID / PENDING to REPOSTED and REBILL
    const invoiceIds: number[] = [];
    invoiceIds.push(this.invoice.id);

    // update invoice status
    this.payerBillingService.updateInvoicesStatus('REPOSTED', invoiceIds).subscribe({
      next: () =>
      {
        // Prepare items to update        
        this.items = [];
        this.selectedInvoiceItemsRebuildId.forEach(item =>
        {
          const dataIndex = this.gridView.data.findIndex(x => x.id === item);
          if (dataIndex > -1)
          {
            this.items.push(
              {
                itemId: this.gridView.data[dataIndex].id,
                status: 'REBILL'
              });
          }
        });

        // update invoice item status for all items
        this.payerBillingService.updateInvoiceItemsStatuses(this.invoice.id, this.items).subscribe({
          next: () =>
          {
            if (this.items.length === 1)
            {
              this.messageHandlerService.displaySuccess('RECORD-REBILLED-SUCCESSFULLY');
            }
            else
            {
              this.messageHandlerService.displaySuccess('RECORDS-REBILLED-SUCCESSFULLY');
            }

            this.rebillInvoiceItemsDialogOpened = false;
            this.clearSelectedItems();
            this.state.skip = 0;
            this.executeQuery();
          },
          error: (error) =>
          {
            this.messageHandlerService.displayError(error);
          }
        });
      },
      error: (errorResponse) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      }
    });
  }

  public cancelRebillInvoiceItems(): void
  {
    this.rebillInvoiceItemsDialogOpened = false;
  }

  public openRebillInvoiceItemsDialogOpened(): void
  {
    if (this.invoice.invoiceStatus !== 'PROCESSED')
    {
      this.messageHandlerService.displayDirectMessage('error', this.rebillRestriction);
      return;
    }
    this.rebillInvoiceItemsDialogOpened = true;
  }

  /**
  * Perform grid refresh.
  */
  public refresh(): void
  {
    this.loadInvoiceItems(this.invoice.id);
  }

  /**
   * Handles the edit event of the code grid.
   */
  public invoiceItemEditHandler(event: GridEventHandler): void
  {
    this.editedInvoiceItem = event.dataItem;
    if (event.dataItem.billableServiceHasManualAmount) // edit billed amount, do not edit rate
    {
      this.invoiceItemFormGroup = this.formBuilder.group(
        {
          billedAmount: event.dataItem.billedAmount,
          diagnosisCodes: event.dataItem.diagnosisCodes,
          units: event.dataItem.units,
          editing: true
        }
      );
    }
    else // edit rate, do not edit billed amount
    {
      this.invoiceItemFormGroup = this.formBuilder.group(
        {
          rate: event.dataItem.rate,
          diagnosisCodes: event.dataItem.diagnosisCodes,
          units: event.dataItem.units,
          editing: true
        }
      );
    }

    event.sender.editRow(event.rowIndex, this.invoiceItemFormGroup);
  }

  /**
   * Saves the edited invoice item from the grid.
   *
   * @param event Save row edit event from the Kendo grid for Invoice Item.
   */
  public updateInvoiceItem(event): void  
  {
    if (this.editedInvoiceItem.billableServiceHasManualAmount && !this.invoiceItemFormGroup.value.billedAmount)
    {
      this.messageHandlerService.displayDirectMessage('error',
        this.textAmountRequired.replace(`for '{{ value }}'`, 'null'));
      event.sender.closeRow(event.rowIndex);
    }
    else
      if (!this.editedInvoiceItem.billableServiceHasManualAmount && (!this.invoiceItemFormGroup.value.units || !this.invoiceItemFormGroup.value.rate))
      {
        if (!this.invoiceItemFormGroup.value.units && !this.invoiceItemFormGroup.value.rate)
        {
          this.messageHandlerService.displayDirectMessage('error',
            this.textUnitsRateRequired.replace("for '{{value}}'", ""));
        }
        else
          if (!this.invoiceItemFormGroup.value.units)
          {
            this.messageHandlerService.displayDirectMessage('error',
              this.textUnitsRequired.replace("for '{{value}}'", ""));
          }
          else
            if (!this.invoiceItemFormGroup.value.rate)
            {
              this.messageHandlerService.displayDirectMessage('error',
                this.textRateRequired.replace("for '{{value}}'", ""));
            }
        event.sender.closeRow(event.rowIndex);
      }
      else
      {
        const invoiceItem = this.invoiceItemFormGroup.value;

        // editable
        invoiceItem.diagnosisCodes = this.invoiceItemFormGroup.value.diagnosisCodes;
        invoiceItem.units = this.invoiceItemFormGroup.value.units;
        if (this.editedInvoiceItem.billableServiceHasManualAmount)
        {
          invoiceItem.billedAmount = this.invoiceItemFormGroup.value.billedAmount;
          invoiceItem.rate = this.editedInvoiceItem.rate; // not editable
        }
        else
        {
          invoiceItem.rate = this.invoiceItemFormGroup.value.rate;
          invoiceItem.billedAmount = invoiceItem.units * invoiceItem.rate; // not editable
        }

        // not editable
        invoiceItem.id = this.editedInvoiceItem.id;
        invoiceItem.dateOfService = this.editedInvoiceItem.dateOfService.toISOString().substring(0, 10);
        invoiceItem.firstName = this.editedInvoiceItem.firstName;
        invoiceItem.invoiceId = this.editedInvoiceItem.invoiceId;
        invoiceItem.lastName = this.editedInvoiceItem.lastName;
        invoiceItem.medicaidId = this.editedInvoiceItem.medicaidId;
        invoiceItem.payerBillingIdentifier = this.editedInvoiceItem.payerBillingIdentifier;
        invoiceItem.modifierCode = this.editedInvoiceItem.modifierCode;
        invoiceItem.modifierCode2 = this.editedInvoiceItem.modifierCode2;
        invoiceItem.modifierCode3 = this.editedInvoiceItem.modifierCode3;
        invoiceItem.serviceCode = this.editedInvoiceItem.serviceCode;
        invoiceItem.feeScheduleId = this.editedInvoiceItem.feeScheduleId;
        invoiceItem.patientId = this.editedInvoiceItem.patientId;
        invoiceItem.caseBillableId = this.editedInvoiceItem.caseBillableId;
        invoiceItem.billableServiceHasManualAmount = this.editedInvoiceItem.billableServiceHasManualAmount;
        invoiceItem.errorMessage = this.editedInvoiceItem.errorMessage;
        const newStatus = this.payerBillingService.invoiceItemStatuses.find((x: any) => (this.canRebuildInvoiceItem(x.value))).ordinalValue;
        invoiceItem.invoiceItemStatus = newStatus;
        invoiceItem.valid = true;

        delete invoiceItem.editing;

        this.payerBillingService.updateInvoiceItem(this.editedInvoiceItem.invoiceId, invoiceItem).subscribe({
          next: (data: any) =>
          {
            this.messageHandlerService.displaySuccess('RECORD-SAVED-SUCCESSFULLY');

            // close the editing row, and update the grid.
            event.dataItem = data;

            this.setInvoiceItemFields(data);
            this.view.saveHandler(event);

            // update case billable

            const caseBillable: any = {};
            caseBillable.id = this.invoiceItemFormGroup.value.caseBillableId;
            caseBillable.billableServiceHasManualAmount = this.invoiceItemFormGroup.value.billableServiceHasManualAmount;
            caseBillable.units = this.invoiceItemFormGroup.value.units;
            caseBillable.diagnoses = this.invoiceItemFormGroup.value.diagnosisCodes;
            if (caseBillable.billableServiceHasManualAmount)
            {
              caseBillable.amount = this.invoiceItemFormGroup.value.billedAmount;
            }
            else
            {
              caseBillable.rate = this.invoiceItemFormGroup.value.rate;
            }
            caseBillable.caseBillableStatus = newStatus;

            this.caseService.updateCaseBillable(caseBillable, caseBillable.id, this.invoiceItemFormGroup.value.patientId).subscribe({
              next: () =>
              {
                //this.messageHandlerService.displaySuccess('CASE-BILLABLE-SAVED-SUCCESSFULLY');                      
              },
              error: (error) =>
              {
                this.messageHandlerService.displayError(error);
              }
            });
          },
          error: (error) =>
          {
            this.messageHandlerService.displayError(error);
          }
        });
      }
  }

  public deleteHandler(dataItem: any): void
  {
    this.selectedInvoiceItemId = dataItem.id;
    this.selectedInvoiceItemName = dataItem.firstName.concat(' ').concat(dataItem.lastName).concat(' (').concat(dataItem.dateOfService).concat(') - ').concat(dataItem.invoiceItemStatusText);
    this.open('deleteDialog');
  }

  public deleteAction(confirm: boolean): void
  {
    if (confirm)
    {
      // Proceed with the deletion
      const invoiceItemId = this.selectedInvoiceItemId;
      this.payerBillingService.deleteInvoiceItem(invoiceItemId).subscribe({
        next: () =>
        {
          this.refresh();
          this.messageHandlerService.displaySuccess('RECORD-DELETED-SUCCESSFULLY');
        },
        error: (errorResponse) =>
        {
          this.messageHandlerService.displayError(errorResponse);
        }
      });
    }
    this.close('deleteDialog');
    this.selectedInvoiceItemId = null;
    this.selectedInvoiceItemName = null;
  }

  /**
  * Opens a component
  *
  * @param component Name of component to be opened.
  */
  public open(component: string): void
  {
    this[component + 'Opened'] = true;
  }

  /**
  * Closes a component
  *
  * @param component Name of component to be closed.
  */
  public close(component: string): void
  {
    this[component + 'Opened'] = false;
  }

  public onSelectionChange(event: SelectionEvent): void
  {
    if (event.selectedRows[0]?.dataItem.canRebuildInvoiceItem)
    {
      if (!this.selectedInvoiceItemsRebuildId.includes(event.selectedRows[0].dataItem.id))
      {
        this.selectedInvoiceItemsRebuildId = [...this.selectedInvoiceItemsRebuildId, event.selectedRows[0].dataItem.id];
      }
    }
    else if (event?.deselectedRows)
    {
      this.selectedInvoiceItemsRebuildId = this.selectedInvoiceItemsRebuildId.filter((k: any) => k !== event?.deselectedRows[0]?.dataItem.id);
    }
  }

  public canRebuildInvoiceItem(invoiceItemStatus: string): boolean
  {
    return invoiceItemStatus === 'PENDING_REBILL';
  }

  public allData(): Promise<ExcelExportData>
  {
    return new Promise((resolve, reject) =>
    {
      this.excelFileName = `Payer Billing Records-${this.invoice.invoiceNumber}.xlsx`;
      if (this.state !== undefined)
      {
        const currentTake = this.state.take;
        this.state.take = this.gridView.total;

        const httpPostBody = GridQueryUtils.normalizeFilters(this.state);

        this.payerBillingItemListService.query(
          (items: any) =>
          {
            if (items)
            {
              items.data.forEach((d: any) => { this.setInvoiceItemFields(d); });

              const column = this.view.columnsConfig.find(col => col.field === 'payerName');
              let changePayerHidden = false;
              if (column && column.hidden === true)
              {
                column.hidden = false;
                changePayerHidden = true;
              }

              this.count = items.total;
              this.gridView = { data: items.data, total: items.total };
              this.view.setData(items.data);

              const result: ExcelExportData = {
                data: process(this.view.data, this.state).data
              };

              // Call allData() again with the original "take" value after resolving
              this.allDataWithOriginalTake(currentTake, changePayerHidden).then(() => resolve(result)).catch(reject);
            } else
            {
              reject(new Error("No data received"));
            }
          }, '', null, null, 'post', httpPostBody);
      }
    });
  }

  private allDataWithOriginalTake(originalTake: number, changePayerHidden: boolean): Promise<void>
  {
    return new Promise((resolve, reject) =>
    {
      this.state.take = originalTake; // Restore the original "take" value

      const httpPostBody = GridQueryUtils.normalizeFilters(this.state);

      this.payerBillingItemListService.query(
        (items: any) =>
        {
          if (items)
          {
            items.data.forEach((d: any) => { this.setInvoiceItemFields(d); });

            if (changePayerHidden)
            {
              this.view.columnsConfig.find(col => col.field === 'payerName').hidden = true;
            }

            this.count = items.total;
            this.gridView = { data: items.data, total: items.total };
            this.view.setData(items.data);

            resolve();
          } else
          {
            reject(new Error("No data received"));
          }
        }, '', null, null, 'post', httpPostBody);
    });
  }

  /**
   * Execute invoice list query
   */
  private executeQuery(): void
  {
    const httpPostBody = GridQueryUtils.normalizeFilters(this.state);
    this.payerBillingItemListService.query((items: any) =>
    {
      items.data.forEach((d: any) => { this.setInvoiceItemFields(d); });

      this.count = items.total;
      this.gridView = { data: items.data, total: items.total };
      this.view.setData(items.data);
      this.allData = this.allData.bind(this);
    }, '', null, null, 'post', httpPostBody);
  }

  /**
   * Grid columns initialization
   */
  private initGridColumns(): void
  {
    this.translateService.get(['ID', 'CASE-ID-NUMBER', 'MEDICAID-ID', 'PATIENT-ID',
      'FIRST-NAME', 'LAST-NAME', 'DATE-OF-SERVICE', 'SERVICE-CODE', 'MODIFIER-CODE-1', 'MODIFIER-CODE-2', 'MODIFIER-CODE-3', 'DIAGNOSIS-CODE', 'UNITS', 'BILLED-AMOUNT', 'IS-VALID', 'MESSAGES',
      'RECORD-STATUS', 'FEE-SCHEDULE', 'CREATED-BY', 'CREATED-DATE', 'LAST-MODIFIED-BY', 'LAST-MODIFIED-DATE', 'REBILL-RESTRICTION', 'RATE', 'IDENTIFIER', 'PAYER-NAME',
      'CODE', 'DESCRIPTION'
    ]).subscribe({
      next: (res) =>
      {
        this.view.columnsConfig = [
          { field: 'id', title: res.ID, titleKeys: ['ID'], filter: 'numeric', format: '{0:n}', hidden: true, groupNumber: 0, width: 50 },
          {
            field: 'caseId', title: res['CASE-ID-NUMBER'], titleKeys: ['CASE-ID-NUMBER'], filter: 'numeric', format: '{0:n}', hidden: false, groupNumber: 0, width: 50
          },
          {
            field: 'patientId', title: res['PATIENT-ID'].replace('{{patientLabel}}', this.patientLabelText),
            titleKeys: ['PATIENT-ID'], filter: 'numeric', format: '{0:n}', hidden: true, width: 50, groupNumber: 0
          },
          {
            field: 'payerName', title: res['PAYER-NAME'], titleKeys: ['PAYER-NAME'], hidden: true, width: 60, groupNumber: 0
          },
          {
            field: 'medicaidId', title: res['MEDICAID-ID'], titleKeys: ['MEDICAID-ID'], hidden: false, width: 60, groupNumber: 0
          },
          {
            field: 'firstName', title: res['FIRST-NAME'], titleKeys: ['FIRST-NAME'], hidden: false, width: 60, groupNumber: 0
          },
          {
            field: 'lastName', title: res['LAST-NAME'], titleKeys: ['LAST-NAME'], hidden: false, width: 60, groupNumber: 0
          },
          {
            field: 'payerBillingIdentifier', title: this.payerBillingIdentifierName, titleKeys: ['IDENTIFIER'], hidden: this.payerBillingIdentifierName === '', width: 60, groupNumber: 0
          },
          {
            field: 'dateOfService', title: res['DATE-OF-SERVICE'], titleKeys: ['DATE-OF-SERVICE'], hidden: false, width: 70,
            format: '{0:d}', filter: 'date', groupNumber: 0
          },
          {
            field: 'serviceCode', title: res['SERVICE-CODE'], titleKeys: ['SERVICE-CODE'], hidden: false, width: 60, groupNumber: 0
          },
          {
            field: 'modifierCode', title: res['MODIFIER-CODE-1'], titleKeys: ['MODIFIER-CODE-1'], hidden: false, width: 60, groupNumber: 0
          },
          {
            field: 'modifierCode2', title: res['MODIFIER-CODE-2'], titleKeys: ['MODIFIER-CODE-2'], hidden: false, width: 60, groupNumber: 0
          },
          {
            field: 'modifierCode3', title: res['MODIFIER-CODE-3'], titleKeys: ['MODIFIER-CODE-3'], hidden: false, width: 60, groupNumber: 0
          },
          {
            field: 'diagnosisCodes', title: res['DIAGNOSIS-CODE'], titleKeys: ['DIAGNOSIS-CODE'], hidden: false, width: 70, groupNumber: 0
          },
          {
            field: 'units', title: res.UNITS, titleKeys: ['UNITS'], filter: 'numeric', format: '{0:n}', hidden: false, width: 45, groupNumber: 0, class: 'text-right'
          },
          {
            field: 'rate', title: res.RATE, titleKeys: ['RATE'], filter: 'numeric', format: 'n2', hidden: false, width: 70, groupNumber: 0, class: 'text-right'
          },
          {
            field: 'billedAmount', title: res['BILLED-AMOUNT'], titleKeys: ['BILLED-AMOUNT'], filter: 'numeric', format: 'n2', hidden: false, width: 70, groupNumber: 0, class: 'text-right'
          },
          { field: 'feeScheduleName', title: res['FEE-SCHEDULE'], titleKeys: ['FEE-SCHEDULE'], filterable: true, sortable: false, hidden: true, width: 80, groupNumber: 1 },
          { field: 'invoiceItemStatus', title: res['RECORD-STATUS'], titleKeys: ['RECORD-STATUS'], filterable: true, hidden: false, width: 80, groupNumber: 1 },
          { field: 'remarkCode', title: res.CODE, titleKeys: ['CODE'], filterable: true, hidden: false, width: 80, groupNumber: 1 },
          { field: 'remarkDescription', title: res.DESCRIPTION, titleKeys: ['DESCRIPTION'], filterable: true, hidden: false, width: 80, groupNumber: 1 },
          { field: 'valid', title: res['IS-VALID'], titleKeys: ['IS-VALID'], filter: 'boolean', hidden: false, width: 45, groupNumber: 0 },
          { field: 'errorMessage', title: res.MESSAGES, titleKeys: ['MESSAGES'], hidden: false, width: 80, groupNumber: 0 },
          { field: 'createdBy', title: res['CREATED-BY'], titleKeys: ['CREATED-BY'], hidden: true, groupNumber: 0 },
          {
            field: 'createdOn', title: res['CREATED-DATE'], titleKeys: ['CREATED-DATE'], hidden: true,
            format: '{0:g}', filter: 'date', groupNumber: 0
          },
          { field: 'lastModifiedBy', title: res['LAST-MODIFIED-BY'], titleKeys: ['LAST-MODIFIED-BY'], hidden: true, groupNumber: 0 },
          {
            field: 'lastModifiedOn', title: res['LAST-MODIFIED-DATE'], titleKeys: ['LAST-MODIFIED-DATE'], hidden: true,
            format: '{0:g}', filter: 'date', groupNumber: 0
          },
        ];

        // If there is a previously saved grid settings, use it.
        const gridSettings: GridSettings = this.gridStatePersistingService.get('grid-settings-invoice-item');
        if (gridSettings !== null)
        {
          const loadedColumns = GridQueryUtils.setBaseColumnsSetting(this.translateService,
            JSON.stringify(gridSettings.columnsConfig), this.view.columnsConfig, this.patientLabelText);
          this.view.columnsConfig = loadedColumns;

          if (gridSettings.state && gridSettings.state.filter && gridSettings.state.filter.filters)
          {
            GridQueryUtils.convertFiltersToJSDates(gridSettings.state.filter.filters, this.view.columnsConfig);
          }
          this.view.state = gridSettings.state;
        }

        this.rebillRestriction = res['REBILL-RESTRICTION'];
      }
    });
  }

  private loadInvoiceItems(id: number): void 
  {
    this.clearSelectedItems();
    // Load patient import batch id
    this.payerBillingService.getInvoiceById(id).subscribe({
      next: (data: any) =>
      {
        // Load grid data
        this.state = {
          filter: {
            filters: [
              { field: 'invoiceId', operator: 'eq', value: data.id },
              { field: 'deleted', operator: 'eq', value: false }
            ], logic: 'and'
          }, group: [], skip: 0,
          sort: [
            { field: 'lastName', dir: 'asc' }
          ],
          take: this.appConfigService.getConfig().pageSize
        };
        this.executeQuery();
      },
      error: (errorResponse) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      }
    });
  }

  private clearSelectedItems(): void
  {
    this.selectedRecordKeys = [];
    this.selectedInvoiceItemsRebuildId = [];
    this.selectAllState = 'unchecked';
  }

  private setInvoiceItemFields(data: any): void 
  {
    data.lastModifiedOn = this.intl.formatDate(new Date(data.lastModifiedOn), 'g');
    data.createdOn = this.intl.formatDate(new Date(data.createdOn), 'g');
    if (data.dateOfService !== null && data.dateOfService !== undefined && data.dateOfService.length === 10)
    {
      data.dateOfService = new Date(`${data.dateOfService}T12:00:00.000`);
    }
    data.invoiceItemStatusText = this.payerBillingService.getInvoiceItemStatusText(data.invoiceItemStatus);
    data.canRebuildInvoiceItem = this.canRebuildInvoiceItem(data.invoiceItemStatus);
  }

  /**
   * Persit grid columns into storage.
   */
  private saveGrid(): void
  {
    const gridConfig: GridSettings = { columnsConfig: this.view.columnsConfig, state: this.view.state };
    this.gridStatePersistingService.set('grid-settings-invoice-item', gridConfig);
  }
}
